import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, viewChild } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { StateService } from "../../services/state.service";
import { Item } from "../../pojo/Item";
import { NameService } from "../../services/name.service";
import { S25RouterLinkDirective } from "../s25-nav-bar/s25.router.link.directive";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { S25Icons } from "../s25-icon/s25.icon.const";
import { S25CollapseChevronComponent } from "../s25-collapse-chevron/s25.collapse.chevron.component";
import { Connect25SystemAlertsCoordinatorStore } from "./c25-system-alerts/connect25.system.alerts.coordinator";
import { RouteHistory } from "../../services/state.service";
import { S25States } from "../../services/state/state.map";
import { StateStore } from "../../services/state/state.store";
import { s25Resource } from "../../util/s25.resource";
import { S25Util } from "../../util/s25-util";
import { SeSidebarStore } from "./se.sidebar.store";

@TypeManagerDecorator("se-breadcrumbs")
@Component({
    selector: "se-breadcrumbs",
    template: `
        <button
            class="sidebar-toggle"
            [class.sidebar-open]="sidebarStore.isOpen()"
            (click)="sidebarStore.toggle()"
            [attr.aria-expanded]="sidebarStore.isOpen()"
            aria-controls="se-sidebar"
        >
            <s25-ng-icon [type]="'menuRightArrow'" [size]="24" [ariaLabel]="'Menu'" />
            <span aria-hidden="true">Menu</span>
        </button>
        <nav class="breadcrumb-nav" aria-label="Breadcrumb">
            <ol class="breadcrumb-list">
                <li class="breadcrumb-item">
                    <a s25-ng-router-link [stateName]="'seHomeView'" class="breadcrumb-link home-link">
                        <s25-ng-icon [type]="'dashboard'" [size]="14" [ariaLabel]="false" />
                        <span>Dashboard</span>
                    </a>
                </li>

                @if (routeHistory().length) {
                    @for (route of routeHistory(); track route; let isLast = $last) {
                        @let stateName = getRouteToIdentifier(route);
                        @let objData = getObjData(stateName);
                        @let nameKey = route.to._params.itemId + "-" + $safeNavigationMigration(objData?.itemTypeId);

                        <li class="breadcrumb-separator" aria-hidden="true">
                            <s25-ng-icon [type]="'caretRight'" [size]="12" [ariaLabel]="false" />
                        </li>

                        @let current = isLast && isObjectView();
                        <li class="breadcrumb-item" [class.current]="current">
                            @if (!current) {
                                <a
                                    s25-ng-router-link
                                    [stateName]="route.to._identifier"
                                    [stateParams]="{ itemId: +route.to._params.itemId! }"
                                    class="breadcrumb-link"
                                >
                                    <s25-ng-icon
                                        [type]="objData?.iconProp"
                                        [size]="14"
                                        [autoDarkMode]="true"
                                        [ariaLabel]="false"
                                    />
                                    <span>{{ nameMap()![nameKey] }}</span>
                                </a>
                            } @else {
                                <span class="breadcrumb-current">
                                    <s25-ng-icon
                                        [type]="objData?.iconProp"
                                        [size]="14"
                                        [autoDarkMode]="true"
                                        [ariaLabel]="false"
                                    />
                                    <span>{{ nameMap()![nameKey] }}</span>
                                </span>
                            }
                        </li>
                    }
                }
            </ol>
        </nav>

        @if (alertsCoordinator.bannerAlertCount() > 0) {
            <button
                #alertsToggle
                type="button"
                class="breadcrumb-alerts-toggle"
                [attr.aria-expanded]="!alertsCoordinator.bannerCollapsed()"
                aria-controls="connect25-alerts-region"
                (click)="toggleAlerts($event)"
            >
                <s25-ng-icon
                    class="breadcrumb-alerts-toggle__icon"
                    [class.is-pulsing]="alertsCoordinator.bannerCollapsed()"
                    [type]="'warning'"
                    [size]="16"
                    [ariaLabel]="false"
                />
                <span>{{ alertToggleLabel() }}</span>
                <s25-ng-collapse-chevron
                    [expanded]="!alertsCoordinator.bannerCollapsed()"
                    [collapsedOrientation]="'down'"
                />
            </button>
        }
    `,
    host: {
        class: "breadcrumb-container",
        "(document:keydown.escape)": "onEscapeKey()",
    },
    styles: `
        :host {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.625rem 1.25rem;
            background: var(--cn-breadcrumb-bar-bg);
            color: var(--cn-breadcrumb-current);
        }

        .sidebar-toggle {
            display: flex;
            align-items: center;
            gap: 0.25rem;
            flex-shrink: 0;
            background: none;
            border: none;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            cursor: pointer;
            color: var(--cn-breadcrumb-muted);
            font-size: 0.875rem;
            font-weight: 500;
            transition:
                color var(--transition-short) ease,
                background-color var(--transition-short) ease;
        }

        .sidebar-toggle:hover {
            color: var(--cn-breadcrumb-strong);
            background-color: var(--cn-breadcrumb-link-hover-bg);
        }

        .sidebar-toggle.sidebar-open s25-ng-icon {
            --icon--rotation-degrees: 180deg;
        }

        .breadcrumb-nav {
            flex: 1;
            min-width: 0;
        }

        .breadcrumb-alerts-toggle {
            display: flex;
            align-items: center;
            gap: 0.375rem;
            flex-shrink: 0;
            background: none;
            border: none;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            cursor: pointer;
            color: var(--cn-breadcrumb-muted);
            font-size: 0.8125rem;
            font-weight: 500;
            transition:
                color var(--transition-short) ease,
                background-color var(--transition-short) ease;
        }

        .breadcrumb-alerts-toggle:hover {
            color: var(--cn-breadcrumb-strong);
            background-color: var(--cn-breadcrumb-link-hover-bg);
        }

        .breadcrumb-alerts-toggle:focus-visible {
            outline: 2px solid var(--cn-breadcrumb-muted);
            outline-offset: 1px;
        }

        .breadcrumb-alerts-toggle s25-ng-icon {
            flex-shrink: 0;
            color: var(--color--danger);
        }

        .breadcrumb-alerts-toggle__icon {
            display: inline-flex;
        }

        @keyframes breadcrumb-alerts-pulse {
            0%,
            100% {
                transform: scale(1);
                opacity: 1;
            }
            50% {
                transform: scale(1.18);
                opacity: 0.55;
            }
        }

        @media (prefers-reduced-motion: no-preference) {
            .breadcrumb-alerts-toggle__icon.is-pulsing {
                animation: breadcrumb-alerts-pulse 1.6s ease-in-out infinite;
            }
        }

        .breadcrumb-list {
            display: flex;
            align-items: center;
            margin: 0;
            padding: 0;
            list-style: none;
            font-size: 0.8125rem;
        }

        .breadcrumb-item {
            display: inline-flex;
            align-items: center;
        }

        .breadcrumb-separator {
            display: inline-flex;
            align-items: center;
            margin: 0 0.375rem;
            color: var(--cn-breadcrumb-separator);
        }

        .breadcrumb-link,
        .breadcrumb-current {
            display: inline-flex;
            align-items: center;
            gap: 0.375rem;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            transition:
                color var(--transition-short) ease,
                background-color var(--transition-short) ease;
        }

        .breadcrumb-link {
            color: var(--cn-breadcrumb-muted);
            text-decoration: none;
        }

        .breadcrumb-link:hover {
            color: var(--cn-breadcrumb-strong);
            background-color: var(--cn-breadcrumb-link-hover-bg);
        }

        .breadcrumb-link:focus-visible {
            outline: 2px solid var(--cn-breadcrumb-muted);
            outline-offset: 1px;
        }

        .home-link {
            color: var(--cn-breadcrumb-strong);
            font-weight: 500;
        }

        .home-link:hover {
            color: var(--cn-breadcrumb-current);
        }

        .breadcrumb-current {
            color: var(--cn-breadcrumb-current);
            font-weight: 500;
            pointer-events: none;
        }

        .breadcrumb-item.current {
            max-width: 200px;
        }

        .breadcrumb-current span {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .breadcrumb-link s25-ng-icon,
        .breadcrumb-current s25-ng-icon {
            flex-shrink: 0;
            opacity: 0.7;
        }

        .breadcrumb-current s25-ng-icon {
            opacity: 0.85;
        }

        @media (max-width: 930px) {
            .sidebar-toggle {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 40px;
                height: 40px;
                border-radius: 10px;
                background: var(--cn-navy-100);
                transition: background var(--transition-medium) ease;
            }

            :host .sidebar-toggle ::ng-deep svg {
                width: 22px !important;
                height: 22px !important;
                color: var(--cn-navy);
            }

            .breadcrumb-list {
                font-size: 1rem;
            }

            :host .breadcrumb-link ::ng-deep svg,
            :host .breadcrumb-current ::ng-deep svg {
                width: 16px !important;
                height: 16px !important;
            }
        }

        @media (max-width: 640px) {
            :host {
                padding: 0.5rem 1rem;
                align-items: flex-start;
            }

            .sidebar-toggle span {
                display: none;
            }

            .sidebar-toggle {
                width: 44px;
                height: 44px;
            }

            :host .sidebar-toggle ::ng-deep svg {
                width: 24px !important;
                height: 24px !important;
            }

            .breadcrumb-list {
                flex-wrap: wrap;
            }

            .breadcrumb-link,
            .breadcrumb-current {
                padding: 0.125rem 0.375rem;
                gap: 0.25rem;
            }

            :host .breadcrumb-link ::ng-deep svg,
            :host .breadcrumb-current ::ng-deep svg {
                width: 20px !important;
                height: 20px !important;
            }

            .breadcrumb-separator {
                margin: 0 0.25rem;
            }

            .breadcrumb-separator:last-of-type {
                display: none;
            }

            .breadcrumb-item:last-child {
                margin-top: 0.25rem;
            }

            .home-link span {
                display: none;
            }

            .breadcrumb-item:not(:first-child):not(:last-child) .breadcrumb-link span {
                display: none;
            }

            .breadcrumb-item:not(:first-child):not(:last-child) .breadcrumb-link {
                padding: 0.25rem;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25RouterLinkDirective, S25IconComponent, S25CollapseChevronComponent],
})
export class SeBreadcrumbComponent {
    private stateStore = inject(StateStore);
    readonly sidebarStore = inject(SeSidebarStore);
    readonly alertsCoordinator = inject(Connect25SystemAlertsCoordinatorStore);
    protected getRouteToIdentifier = StateService.getRouteToIdentifier;

    protected readonly alertToggleLabel = computed(() => {
        const count = this.alertsCoordinator.bannerAlertCount();
        return count === 1 ? "1 alert" : `${count} alerts`;
    });

    private readonly alertsToggle = viewChild<ElementRef<HTMLButtonElement>>("alertsToggle");

    /** Stop propagation so the opening click cannot reach the overlay's click-outside listener as it mounts. */
    protected toggleAlerts(event: Event): void {
        event.stopPropagation();
        this.alertsCoordinator.toggleBannerCollapsed();
    }

    protected onEscapeKey(): void {
        if (this.alertsCoordinator.bannerCollapsed()) return;
        this.alertsCoordinator.collapseBanner();
        this.alertsToggle()?.nativeElement.focus();
    }

    private allRouteHistory = computed(() => this.stateStore.routeHistory());

    protected isObjectView = computed(() => {
        const routes = this.allRouteHistory(),
            mostRecent = this.getRouteToIdentifier(routes?.at(-1));
        return (
            !!mostRecent &&
            [S25States.names.studentEngagementEvent, S25States.names.studentEngagementOrganization].some((name) =>
                mostRecent.includes(name),
            )
        );
    });

    protected routeHistory = computed<RouteHistory[]>(() => {
        const allRoutes = this.allRouteHistory();
        const filteredRoutes =
                allRoutes?.filter(
                    (route: RouteHistory) => {
                        const stateName = this.getRouteToIdentifier(route);
                        return !!stateName && stateName in this.objIdMap;
                    }, // object type, not home
                ) ?? [],
            seen = new Set<string>(),
            mostRecentRoutes = filteredRoutes.slice(-5);
        return mostRecentRoutes
            .reverse() // filter in reverse to keep most recent state
            .filter((route: RouteHistory) => {
                // no duplicates
                const key = `${route.to._identifier}-${route.to._params.itemId}`;
                if (seen.has(key)) return false;
                else {
                    seen.add(key);
                    return true;
                }
            })
            .reverse();
    });

    objIdMap: Partial<Record<S25States.Name, { itemTypeId: Item.Ids; iconProp: S25Icons.Name }>> = {
        [S25States.names.studentEngagementEventDetails]: {
            itemTypeId: Item.Ids.Event,
            iconProp: S25Icons.names.calendarDate,
        },
        [S25States.names.seOrganizationDetails]: {
            itemTypeId: Item.Ids.Organization,
            iconProp: S25Icons.names.organization,
        },
    };

    private objects = computed(() =>
        this.routeHistory()
            .map((route: RouteHistory) => {
                const stateName = this.getRouteToIdentifier(route);
                if (stateName)
                    return { itemId: +route.to._params.itemId!, itemTypeId: this.objIdMap[stateName]?.itemTypeId };
            })
            .filter(S25Util.isDefined),
    );

    protected nameMap = computed(() => this.nameMapResp.value());

    private nameMapResp = s25Resource({
        defaultValue: {},
        if: () => !!this.objects()?.length,
        params: () => this.objects(),
        loader: async ({ params: objects }) => {
            const [names] = await S25Util.Maybe(NameService.getNames(objects));
            if (names) return names;
            return {};
        },
    });

    protected getObjData(stateName: S25States.Name | undefined) {
        if (!!stateName && stateName in this.objIdMap) {
            return this.objIdMap[stateName];
        }
        return undefined;
    }
}
